import { formatDate, getDaysInMonth, lastDayOfMonth } from "date-fns";
import { Folder, FolderOpen } from "lucide-react";
import * as React from "react";
import { Header } from "src/components/Header";
import { trpc } from "src/trpc";
import { formatCurrency } from "src/utils/currency";
import { Tree } from "src/utils/Tree";
import styled from "styled-components";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useIsoContext } from "src/isoContext";

interface Props {
  month: string;
  setCategoryId(categoryId: string | null): void;
}

const getDay = (date: Date) => parseInt(formatDate(date, "d"));

export function BudgetTab({ month, setCategoryId }: Props) {
  const { isLarge } = useIsoContext();
  const [closed, setClosed] = React.useState<string[]>([]);
  const { data: budget } = trpc.getAggregateSpend.useQuery({
    month,
  });

  const today = new Date();
  const isThisMonth = formatDate(today, "yyyy-MM") === month;
  const drawLine = getDay(today) / getDay(lastDayOfMonth(today));
  const daysInMonth = getDaysInMonth(month + "-01");
  const sparklineWidth = isThisMonth ? (150 / daysInMonth) * today.getDate() : 150;

  const toggleOpen = (id: string) => {
    setClosed(closed.includes(id) ? closed.filter((i) => i !== id) : [...closed, id]);
  };

  return (
    <Container>
      {isLarge && <Header slim title="Budget"></Header>}
      <Table>
        <tbody>
          {Tree.walkWithAncestors(budget || [])
            .toArray()
            .map((cat) => {
              if (!cat.ancestry.every((a) => !closed.includes(a.id))) {
                return null;
              }

              if (cat.amount === 0) {
                return null;
              }

              return (
                <Category key={cat.id}>
                  <td style={{ width: 0 }}>
                    {cat.children.length > 0 && (
                      <Toggle onClick={() => toggleOpen(cat.id)}>
                        {!closed.includes(cat.id) ? <FolderOpen size={14} /> : <Folder size={14} />}
                      </Toggle>
                    )}
                  </td>
                  <Name
                    colSpan={cat.budget ? 1 : 2}
                    $indent={cat.ancestry.length}
                    onClick={() => setCategoryId(cat.name === "Uncategorised" ? null : cat.id)}
                  >
                    <NameStr>{cat.name}</NameStr>
                  </Name>
                  {cat.budget &&
                    (() => {
                      const budgetRemaining = (cat.budget / 150) * sparklineWidth - cat.amount;
                      return (
                        <Remaining $positive={budgetRemaining > 0}>
                          {formatCurrency(budgetRemaining)}
                        </Remaining>
                      );
                    })()}
                  <Spent>{formatCurrency(Math.abs(cat.amount))}</Spent>
                  {isLarge && (
                    <Ratio>
                      <Sparklines
                        data={cat.sparkline}
                        width={sparklineWidth}
                        height={30}
                        svgWidth={sparklineWidth}
                        svgHeight={30}
                        margin={1}
                        max={cat.budget ?? undefined}
                      >
                        <SparklinesLine color="var(--theme-color-primary)" />
                      </Sparklines>
                      {cat.budget ? (
                        <BudgetLine viewBox="0 0 150 30">
                          <mask id="mask">
                            <rect x={0} y={0} height={30} width={sparklineWidth} fill="white" />
                          </mask>
                          <line
                            x1={1}
                            y1={29}
                            x2={149}
                            y2={1}
                            style={{ strokeWidth: 1, stroke: "red" }}
                            mask="url(#mask)"
                          />
                        </BudgetLine>
                      ) : null}
                      {isThisMonth && <Line $position={drawLine} />}
                    </Ratio>
                  )}
                </Category>
              );
            })}
        </tbody>
      </Table>
    </Container>
  );
}

const Container = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const Table = styled.table`
  margin-bottom: 20px;
  width: 100%;
  white-space: nowrap;
`;

const Category = styled.tr`
  background: var(--box-color);
  margin-top: -1px;
  > td {
    height: 30px;
    line-height: 14px;
    padding-top: 5px;
    padding-bottom: 5px;
  }
  &:nth-child(odd) {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const Toggle = styled.div`
  display: inline-block;
  vertical-align: middle;
  padding-left: 8px;
`;

const Name = styled.td<{ $indent: number }>`
  white-space: nowrap;
  position: relative;
  padding-right: 8px;
  ${({ $indent }) => `padding-left: ${10 + $indent * 20}px !important;`}
`;

const NameStr = styled.div`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  padding: 5px 0;
  font-size: 14px;
`;

const Spent = styled.td`
  color: red;
  padding: 0 10px;
  text-align: right;
  font-family: monospace;
  font-size: 12px;
  width: 1px;
`;

const Remaining = styled.td<{ $positive: boolean }>`
  color: ${({ $positive }) => ($positive ? "green" : "red")};
  padding: 0 8px;
  font-family: monospace;
  font-size: 12px;
  text-align: right;
  width: 1px;
`;

const Ratio = styled.td`
  background: #eee;
  width: 150px;
  position: relative;
  > svg {
    position: absolute;
    left: 0;
    top: 0;
  }
`;

const Line = styled.div<{ $position: number }>`
  position: absolute;
  z-index: 1;
  ${({ $position }) => ($position === 1 ? `right: 0;` : `left: ${$position * 100}%;`)}
  top: 0;
  height: 100%;
  border-left: 1px solid black;
`;

const BudgetLine = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
`;
